import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import AdminDeleteConversionButton from "@/components/AdminDeleteConversionButton";
import { api } from "@/lib/api";
import { Wallet, ShoppingBag, ExternalLink } from "lucide-react";

function fmtMoney(n, currency = "GYD") {
  return `${currency} ${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return "—"; }
}

export default function SourceEarningsPage() {
  const { source } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/dashboard/earnings/by-source`, { params: { source } });
        setData(data);
      } catch {
        setData({ source, total_earned: 0, conversions: [] });
      }
    })();
  }, [source]);

  const currency = data?.conversions?.[0]?.currency || "GYD";

  return (
    <DashboardLayout>
      <div className="mb-8">
        <div className="label-eyebrow text-[#002FA7] flex items-center gap-2">
          <ShoppingBag size={12} /> Source · {source}
        </div>
        <h1 className="text-4xl sm:text-5xl tracking-tight font-black text-[#111827] flex items-center gap-3 flex-wrap">
          <span>{source}</span>
          <a
            href={`https://${source}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#4B5563] hover:text-[#111827]"
            title="Open source site"
          >
            <ExternalLink size={20} />
          </a>
        </h1>
        <p className="text-sm text-[#4B5563] mt-2 max-w-2xl">
          Every commission you've earned from links shared into <span className="font-mono">{source}</span>. Updates automatically when a customer completes a purchase through your link.
        </p>
      </div>

      {data === null ? (
        <div className="card-flat h-40 animate-pulse bg-[#F9FAFB]" />
      ) : (
        <>
          {/* Headline */}
          <div className="card-flat p-6 mb-6" data-testid={`source-headline-${source}`}>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-[#002FA7]/10 text-[#002FA7] flex items-center justify-center"><Wallet size={20} /></div>
                <div>
                  <div className="label-eyebrow text-[#4B5563]">Total earned from {source}</div>
                  <div className="font-display font-black text-3xl text-[#002FA7]">{fmtMoney(data.total_earned, currency)}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="label-eyebrow text-[#4B5563]">Orders attributed</div>
                <div className="font-display font-black text-3xl text-[#111827]">{data.conversion_count || 0}</div>
                <div className="text-xs text-[#9CA3AF] mt-0.5">{fmtMoney(data.total_volume, currency)} order volume</div>
              </div>
            </div>
          </div>

          {/* Conversions table */}
          <div className="card-flat overflow-hidden">
            <div className="px-6 py-4 border-b border-[#E5E7EB]">
              <div className="label-eyebrow text-[#4B5563]">Conversion order history</div>
            </div>
            {(!data.conversions || data.conversions.length === 0) ? (
              <div className="p-12 text-center text-sm text-[#4B5563]" data-testid="source-empty">
                No purchases tracked from {source} yet. When customers buy through your shared link, the orders appear here.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="source-conversions-table">
                  <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                    <tr className="text-left">
                      <th className="px-6 py-3 text-[10px] tracking-wider uppercase font-bold text-[#9CA3AF]">Order</th>
                      <th className="px-6 py-3 text-[10px] tracking-wider uppercase font-bold text-[#9CA3AF] text-right">Order total</th>
                      <th className="px-6 py-3 text-[10px] tracking-wider uppercase font-bold text-[#9CA3AF] text-right">You earned</th>
                      <th className="px-6 py-3 text-[10px] tracking-wider uppercase font-bold text-[#9CA3AF] text-right">When</th>
                      <th className="px-2 py-3 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.conversions.map((c) => (
                      <tr key={c.conversion_id} className="border-b border-[#F3F4F6] hover:bg-[#FAFAFA]">
                        <td className="px-6 py-3 font-mono text-xs text-[#111827]">{c.order_id}</td>
                        <td className="px-6 py-3 text-right font-mono text-[#111827]">{fmtMoney(c.total, c.currency || currency)}</td>
                        <td className="px-6 py-3 text-right font-mono font-bold text-[#166534]">{fmtMoney(c.commission_total || 0, c.currency || currency)}</td>
                        <td className="px-6 py-3 text-right text-xs text-[#4B5563]">{fmtDateTime(c.occurred_at)}</td>
                        <td className="px-2 py-3 text-right">
                          <AdminDeleteConversionButton
                            conversion={c}
                            onDeleted={(id) =>
                              setData((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      conversions: (prev.conversions || []).filter((x) => x.conversion_id !== id),
                                      conversion_count: Math.max(0, (prev.conversion_count || 0) - 1),
                                      total_earned: Math.max(0, (prev.total_earned || 0) - (Number(c.commission_total) || 0)),
                                      total_volume: Math.max(0, (prev.total_volume || 0) - (Number(c.total) || 0)),
                                    }
                                  : prev
                              )
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </DashboardLayout>
  );
}
